import { useCallback, useEffect, useMemo, useState } from 'react';
import chest from '~/assets/icons/chest.svg';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import exp from '~/layouts/mainMenu/assets/images/exp.svg';
import soft from '~/layouts/mainMenu/assets/images/soft.png';
import { rage, useObservable } from '~/rage';
import { CaseItemRarity, numWord, RewardType } from '~/shared';
import {
	setOpenCaseRewardVisible,
	setOpenCaseVisible,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CaseItemColors } from '../CaseItem';
import { OpenCaseItem } from '../OpenCaseItem';
import { TextLabel } from '../TextLabel';
import {
	StyledCaseName,
	StyledCenterLine,
	StyledCloseButton,
	StyledContainer,
	StyledDecBg,
	StyledFastOpenButton,
	StyledInStockContainer,
	StyledItemsContainer,
	StyledLightColor,
	StyledOpenButton,
	StyledShadow,
	StyledWrapper,
} from './OpenCase.styled';

interface ICase {
	id: number;
	type: RewardType;
	image: string;
	color: string;
	isWinningItem?: boolean;
}

const OpenCase = () => {
	const dispatch = useLayoutDispatch();
	const activeCase = useLayoutSelector((state) => state.mainMenu.activeCase);
	const openCaseResult = useLayoutSelector(
		(state) => state.mainMenu.openCaseResult,
	);
	const openCaseVisible = useLayoutSelector(
		(state) => state.modal.openCaseVisible,
	);
	const [items, setItems] = useState<ICase[]>([]);
	const [winningIndex, setWinningIndex] = useState<number | null>(null);
	const [translateX, setTranslateX] = useState('-50%');
	const [randomTranslate, setRandomTranslate] = useState(0);
	const [transitionTime, setTransitionTime] = useState(0);
	const [isOpening, setIsOpening] = useState(false);

	const donateItems = useObservable(rage.inventoryDonate.getDonateOwnedItems());
	const caseDonateId = activeCase?.donateItemId ?? activeCase?.id;
	const ownedCount = useMemo(
		() => donateItems.find((item) => item.id === caseDonateId)?.count || 0,
		[donateItems, caseDonateId],
	);

	const generateItems = useCallback(() => {
		const newItems = Array.from({ length: 201 }, (_, i) => {
			const item =
				activeCase?.items[Math.floor(Math.random() * activeCase?.items.length)];

			let image;

			switch (rage.cases.getRewardById(item?.donateItemId || '').type) {
				case RewardType.MONEY:
					image = soft;
					break;
				case RewardType.EXP:
					image = exp;
					break;
				default:
					image = item?.donateItemId || '';
			}

			return {
				id: i,
				type:
					rage.cases.getRewardById(item?.donateItemId || '').type ||
					RewardType.VEHICLE,
				image: image,
				color: CaseItemColors[item?.rarity || CaseItemRarity.COMMON],
				isWinningItem: false,
			};
		});
		const rarity =
			activeCase?.items.find((item) => item.donateItemId === openCaseResult)
				?.rarity || CaseItemRarity.COMMON;
		const winIndex = 181;
		newItems[winIndex].isWinningItem = true;
		newItems[winIndex].color =
			CaseItemColors[rarity] || CaseItemColors[CaseItemRarity.COMMON];
		newItems[winIndex].type =
			rage.cases.getRewardById(openCaseResult || '').type || RewardType.VEHICLE;
		if (
			rage.cases.getRewardById(openCaseResult || '').type === RewardType.VEHICLE
		) {
			newItems[winIndex].image = openCaseResult || '';
		}
		setItems(newItems);
		setWinningIndex(winIndex);
		setRandomTranslate(Math.floor(Math.random() * 240) - 120);
	}, [activeCase, openCaseResult]);

	const handleOpen = useCallback(() => {
		if (!winningIndex) {
			return;
		}
		setIsOpening(true);
		setTransitionTime(3);
		const itemWidth = 240; // ширина предмета
		const itemMargin = 20; // расстояние между предметами
		const totalItemWidth = itemWidth + itemMargin;
		const targetX = (winningIndex - 100) * totalItemWidth;
		setTranslateX(`calc(-50% - ${targetX + randomTranslate}px)`);
		setTimeout(() => {
			setTransitionTime(1);
			setTranslateX(`calc(-50% - ${targetX}px)`);
			setTimeout(() => {
				dispatch(setOpenCaseVisible(false));
				dispatch(setOpenCaseRewardVisible(true));
			}, 1500);
		}, 3500);
	}, [generateItems, winningIndex, randomTranslate]);

	const handleClose = useCallback(() => {
		dispatch(setOpenCaseVisible(false));
	}, []);

	useEffect(() => {
		if (isOpening) {
			return;
		}

		generateItems();

		if (!openCaseVisible) {
			return;
		}

		handleOpen();
	}, [winningIndex, isOpening, generateItems, openCaseVisible]);

	return (
		<StyledWrapper>
			<StyledShadow />
			<StyledLightColor $color={'#D35FFC'} />
			<StyledCaseName $color={'#D35FFC'}>{activeCase?.name}</StyledCaseName>
			<StyledDecBg />
			<StyledContainer>
				<StyledInStockContainer>
					<TextLabel>
						В наличии <span style={{ fontWeight: 700 }}>{ownedCount ?? 0}</span>{' '}
						{numWord(ownedCount ?? 0, ['Кейс', 'Кейса', 'Кейсов'])}
					</TextLabel>
				</StyledInStockContainer>
				<StyledItemsContainer
					style={{
						transform: `translateX(${translateX})`,
						transition: `transform ${transitionTime}s cubic-bezier(.14,.64,0,1)`,
					}}
				>
					{items.map((item, index) => (
						<OpenCaseItem
							type={item.type}
							image={item.image}
							color={item.color}
							key={index}
						/>
					))}
				</StyledItemsContainer>
				<StyledCenterLine />
				{isOpening ? (
					<StyledFastOpenButton
						tabIndex={-1}
						onClick={() => {
							handleOpen();
							playSound(Sound.ClickButton);
						}}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
					>
						Открыть быстро
					</StyledFastOpenButton>
				) : (
					<StyledOpenButton
						tabIndex={-1}
						onClick={() => {
							handleOpen();
							playSound(Sound.ClickButton);
						}}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
					>
						<img src={chest} />
						<TextLabel>Крутить кейс</TextLabel>
					</StyledOpenButton>
				)}
				<StyledCloseButton
					onClick={() => {
						handleClose();
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={cross} />
				</StyledCloseButton>
			</StyledContainer>
		</StyledWrapper>
	);
};

export { OpenCase };
